import React, { useState, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Share2, Maximize2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { getListing, listFavorites, addFavorite, removeFavorite } from "../api/dealflowApi";
import { useAuth } from "../auth/AuthContext";
import AIAdvisorPanel from "../components/AIAdvisorPanel";
import AIAdvisorMobileFab from "../components/AIAdvisorMobileFab";
import { ShareDealButton } from "../components/ShareDealButton";
import { canUseNativeShare } from "../lib/shareCapability";
import { isPlausibleYield } from "../lib/yieldPlausibility";
import ImageLightbox from "../components/ImageLightbox";
import MarketSignalBadge from "../components/MarketSignalBadge";
import DamageWarnBadge from "../components/DamageWarnBadge";
import FinanceCalculator from "../components/FinanceCalculator";
import LeadModal from "../components/LeadModal";
import MiniMap from "../components/MiniMap";
import ComparablesModal from "../components/ComparablesModal";
import { ProGateCard } from "../components/billing/ProGate";
import { trackFunnel } from "../lib/funnel";
import {
  clearAdvisorReturnState,
  readAdvisorReturnState,
} from "../utils/advisorReturnStore";
import { resolveInsightText } from "../utils/insight";
import { useTranslation } from "react-i18next";
import {
  signalPhrase,
  softPhrase,
  caveatPhrase,
  buildCohortSubtitle,
  buildValidationLine,
  buildValuationExplainer,
} from "../utils/marketSignal";
import {
  hasValidUrl,
  collectImageUrls,
  pickHeroUrl,
  shouldShowUnverifiedBadge,
} from "../utils/image";
import { riskFlagLabels } from "../utils/germanLabels";
import { enumLabel, featureLabel } from "../utils/enumLabels";
import { RentEstimateBlock } from "../components/RentEstimateInfo";
import { apiClient } from "../api/client";

/* -------- helpers -------- */
function Section({ title, children, testid }) {
  return (
    <section data-testid={testid} className="space-y-2">
      <h2 className="text-xs uppercase tracking-widest text-zinc-500">
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}

/* Master-Sprint P1 — Preisverlauf: only REAL data (price_history rows
   + the source's own strikethrough price). Hidden entirely when no
   real data exists. */
function PriceHistorySection({ ulid }) {
  const { t, i18n } = useTranslation("listing");
  const { data } = useQuery({
    queryKey: ["price-history", ulid],
    queryFn: async () =>
      (await apiClient.get(`/listings/${ulid}/price-history`)).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  if (!data?.has_data) return null;
  const fmt = (v) =>
    typeof v === "number" ? `€ ${Math.round(v).toLocaleString("de-AT")}` : "—";
  const locale =
    i18n.language === "hr" ? "hr-HR" : i18n.language === "en" ? "en-GB" : "de-AT";
  const sr = data.source_reduction;
  return (
    <div
      data-testid="price-history-section"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/60 px-5 py-4 space-y-2.5"
    >
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        {t("priceHistory.title")}
      </div>
      {sr && (
        <div
          data-testid="price-history-source-reduction"
          className="flex flex-wrap items-center gap-2 text-sm"
        >
          <span className="text-zinc-500 line-through">{fmt(sr.from_price)}</span>
          <span className="font-semibold text-emerald-300">{fmt(sr.to_price)}</span>
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
            −{Math.round((1 - sr.to_price / sr.from_price) * 100)} %
          </span>
          <span className="text-[11px] text-zinc-500">{t("priceHistory.sourceNote")}</span>
        </div>
      )}
      {data.history.length > 0 && (
        <ul className="space-y-1" data-testid="price-history-rows">
          {data.history.map((h, i) => {
            const down =
              typeof h.prev_price === "number" &&
              typeof h.price === "number" &&
              h.price < h.prev_price;
            return (
              <li
                key={`${h.observed_at}-${i}`}
                className="flex items-center justify-between gap-3 text-xs"
              >
                <span className="text-zinc-500">
                  {h.observed_at
                    ? new Date(h.observed_at).toLocaleDateString(locale, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "—"}
                </span>
                <span className="text-zinc-300 tabular-nums">
                  {typeof h.prev_price === "number" && (
                    <span className="mr-1.5 text-zinc-600 line-through">
                      {fmt(h.prev_price)}
                    </span>
                  )}
                  <span className={down ? "font-semibold text-emerald-300" : "font-semibold text-rose-300"}>
                    {fmt(h.price)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {data.locked && (
        <div className="pt-1" data-testid="price-history-locked">
          <ProGateCard compact surface="price_history" context="price_history" />
        </div>
      )}
    </div>
  );
}

function KvRow({ k, v }) {
  if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-zinc-900/60 last:border-0">
      <span className="text-xs uppercase tracking-wider text-zinc-500">{k}</span>
      <span className="text-sm text-zinc-200 text-right tabular-nums">
        {Array.isArray(v) ? v.join(", ") : String(v)}
      </span>
    </div>
  );
}

function ImageStrip({ images, primary, health, listing, isAdmin = false }) {
  const { t } = useTranslation("listing");
  const allUrls = collectImageUrls(listing);
  const [active, setActive] = useState(pickHeroUrl(listing));
  // Phase H2-followup round-8 — runtime image-fallback. Tracks URLs
  // that errored at runtime; the rendered hero auto-advances through
  // remaining candidates. `active` (user thumbnail click) still wins
  // when it hasn't yet failed.
  const [failedUrls, setFailedUrls] = useState(() => new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const renderUrl = useMemo(() => {
    const ordered = active ? [active, ...allUrls.filter((u) => u !== active)] : allUrls;
    return ordered.find((u) => !failedUrls.has(u)) ?? null;
  }, [active, allUrls, failedUrls]);
  const heroIndex = Math.max(0, allUrls.indexOf(renderUrl));
  const handleHeroError = () => {
    if (!renderUrl) return;
    setFailedUrls((prev) => {
      if (prev.has(renderUrl)) return prev;
      const next = new Set(prev);
      next.add(renderUrl);
      return next;
    });
  };
  const showImage = hasValidUrl(renderUrl);
  // Mobile-Web-Parity Cycle: `? unverified` overlay is admin-only.
  const showUnverifiedBadge =
    isAdmin && shouldShowUnverifiedBadge(health) && showImage;
  return (
    <div className="space-y-3">
      <div className="relative aspect-[4/3] bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800">
        {showImage ? (
          <>
            <img
              src={renderUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
              data-testid="listing-detail-image"
              onError={handleHeroError}
              onClick={() => setLightboxOpen(true)}
            />
            <div
              aria-hidden="true"
              className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-zinc-900/70 border border-zinc-700 text-zinc-200 flex items-center justify-center pointer-events-none"
            >
              <Maximize2 className="h-4 w-4" />
            </div>
            {showUnverifiedBadge && (
              <div
                data-testid="listing-detail-unverified-badge"
                className="absolute top-2 right-2 h-6 px-2 rounded-full bg-zinc-900/80 text-zinc-300 text-xs flex items-center border border-zinc-700"
              >
                ? unverified
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-zinc-600">
            <div className="text-center">
              <div className="text-5xl mb-2 opacity-30">◾</div>
              <div className="text-xs uppercase tracking-widest">
                {health === "dead_on_check" && allUrls.length === 0
                  ? t("imageBroken")
                  : t("noImage")}
              </div>
            </div>
          </div>
        )}
      </div>
      {allUrls.length > 1 && (
        <GalleryThumbs
          urls={allUrls}
          active={active}
          failed={failedUrls}
          onThumbError={(url) =>
            setFailedUrls((prev) => {
              if (prev.has(url)) return prev;
              const next = new Set(prev);
              next.add(url);
              return next;
            })
          }
          onPick={(url) => {
            setActive(url);
            // Phase H2-followup round-8 — an explicit user click
            // overrides a previous runtime failure on this URL so
            // the user can retry a thumb that errored earlier.
            setFailedUrls((prev) => {
              if (!prev.has(url)) return prev;
              const next = new Set(prev);
              next.delete(url);
              return next;
            });
          }}
        />
      )}
      {allUrls.length > 0 && (
        <ImageLightbox
          urls={allUrls}
          open={lightboxOpen}
          startIndex={heroIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * P1 — Galerie Expand/Collapse.
 *
 * When the gallery has more than `COLLAPSED_THRESHOLD` thumbnails
 * (currently 10), only the first `COLLAPSED_SHOW` (8) are rendered by
 * default. A toggle button reveals/hides the rest. The chosen numbers
 * keep the default view at "2 clean rows of 4 thumbs on desktop / 2 rows
 * of 5 on mobile" without exposing the full 50-90-image dump that some
 * Apify-scraped Willhaben listings carry.
 */
const COLLAPSED_THRESHOLD = 10;
const COLLAPSED_SHOW = 8;

function GalleryThumbs({ urls, active, onPick, failed, onThumbError }) {
  const { t } = useTranslation("listing");
  const [expanded, setExpanded] = React.useState(false);
  const needsToggle = urls.length > COLLAPSED_THRESHOLD;
  const visibleUrls = !needsToggle || expanded ? urls : urls.slice(0, COLLAPSED_SHOW);
  const remaining = urls.length - COLLAPSED_SHOW;
  const failedSet = failed ?? new Set();

  return (
    <div className="space-y-2">
      <div
        className="grid grid-cols-4 sm:grid-cols-5 gap-2"
        data-testid="listing-detail-thumbs"
      >
        {visibleUrls.map((url) => {
          // 23.10.2026 — ein Vorschaubild, das nicht lädt, blieb als
          // schwarze Kachel stehen (Nutzerbefund). Jetzt sichtbarer
          // Fehlzustand; ein Klick darauf versucht es erneut.
          const isFailed = failedSet.has(url);
          return (
            <button
              key={url}
              type="button"
              onClick={() => onPick(url)}
              title={isFailed ? t("imageBroken") : undefined}
              aria-label={isFailed ? t("imageBroken") : undefined}
              data-testid={isFailed ? "listing-detail-thumb-failed" : "listing-detail-thumb"}
              className={`aspect-square rounded-md overflow-hidden border transition ${
                active === url
                  ? "border-amber-500"
                  : "border-zinc-800 hover:border-zinc-600"
              }`}
            >
              {isFailed ? (
                <span className="w-full h-full flex items-center justify-center bg-zinc-900 text-zinc-400">
                  <ImageOff className="h-5 w-5" aria-hidden="true" />
                </span>
              ) : (
                <img
                  src={url}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={() => onThumbError?.(url)}
                />
              )}
            </button>
          );
        })}
      </div>
      {needsToggle && (
        <button
          type="button"
          data-testid="gallery-expand-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="w-full text-center px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950/60 text-sm text-zinc-300 hover:border-emerald-400/60 hover:text-emerald-200 transition"
        >
          {expanded
            ? t("showFewerImages")
            : t("showMoreImages", { count: remaining })}
        </button>
      )}
    </div>
  );
}

// Slug → German phrase maps imported from `utils/marketSignal.js`. Kept
// thin so the slug-map source of truth lives in ONE place (shared with
// the DealCard's MarketSignalBadge component).

function MarketSignalBlock({ card }) {
  const { t } = useTranslation("listing");
  const { t: tMarket } = useTranslation("market");
  // V4.18 — Explainable Market Signal: "Vergleichsobjekte ansehen".
  const [compOpen, setCompOpen] = useState(false);
  // M8: market scoring engine is active. Strict > soft > caveat.
  const strict = card.market_signal;
  const soft = card.market_signal_soft;
  const caveat = card.market_signal_caveat;
  // Master-Audit 2026-07 — the DealAlert Insight one-liner now lives
  // INSIDE this card (verdict → evidence → narrative) instead of a
  // near-duplicate second card below.
  const insightText = resolveInsightText(card);
  const vertical = card.vertical === "auto" ? "auto" : "immo";
  if (!strict && !soft && !caveat && !insightText) return null;
  const hasSignal = Boolean(strict || soft || caveat);
  const primary =
    (strict && signalPhrase(strict)) || (soft && softPhrase(soft)) || null;
  const caveatText = caveat ? caveatPhrase(caveat) : null;
  const cohortSubtitleText = buildCohortSubtitle(card);
  // Bewertungsdiagnose 2026-09-28 — plain-language "why" block.
  const explainer = buildValuationExplainer(card);
  // V4.18 — two-tier display: below the cohort line either the
  // broader-layer validation or the cascade-expansion hint.
  const validationLine = buildValidationLine(card);
  const showComparables = (card.comparable_count || 0) > 0;
  return (
    <div
      data-testid="market-signal-block"
      className="border border-zinc-800 bg-zinc-950/60 rounded-2xl p-5 space-y-2"
    >
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        {t("marketSignal")}
      </div>
      {hasSignal && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-zinc-200">{primary || "—"}</div>
          <MarketSignalBadge card={card} full inline={false} />
        </div>
      )}
      {/* Phase H2-followup round-7 — when there's a server-rendered
          subtitle the badge already carries the caveat wording (e.g.
          "deutlich über Vergleich · nur breite Vergleichsbasis ·
          konservativ bewertet") so the standalone amber caveat line
          would just duplicate it. Only render the standalone line as
          a fallback when no subtitle is available. */}
      {caveatText && !card.market_signal_subtitle_key && !card.market_signal_subtitle && (
        <div className="text-xs text-amber-300/80 pt-1">⚠ {caveatText}</div>
      )}
      {/* V4.18 — evaluation cohort line ("8 sehr ähnliche
          Vergleichsobjekte · 📍 300-m-Umkreis"). */}
      {hasSignal && cohortSubtitleText && (
        <div
          data-testid="market-signal-cohort-line"
          className="text-[11px] text-zinc-500"
        >
          {cohortSubtitleText}
        </div>
      )}
      {/* V4.18 — market-validation / expansion subtext. */}
      {hasSignal && validationLine && (
        <div
          data-testid="market-signal-validation-line"
          className="text-[11px] text-zinc-500/90 italic"
        >
          {validationLine}
        </div>
      )}
      {/* Bewertungsdiagnose 2026-09-28 — plain-language explainer:
          comparison basis, reliability and the reason the valuation is
          (or is not) load-bearing. `noDealHint` states explicitly when
          a suppressed signal must NOT be read as a deal. */}
      {explainer && (
        <div
          data-testid="valuation-explainer"
          data-tier={explainer.tier}
          className="mt-1 rounded-xl border border-zinc-800 bg-zinc-900/40 px-3.5 py-3 space-y-1.5"
        >
          <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
            {tMarket("explainer.title")}
          </div>
          <div className="text-xs text-zinc-400" data-testid="valuation-explainer-basis">
            <span className="text-zinc-500">{tMarket("explainer.basisLabel")}: </span>
            {explainer.basis}
          </div>
          <div className="text-xs text-zinc-400" data-testid="valuation-explainer-confidence">
            <span className="text-zinc-500">{tMarket("explainer.confidenceLabel")}: </span>
            {explainer.confidence}
          </div>
          <div className="text-xs text-zinc-300 leading-snug" data-testid="valuation-explainer-reason">
            <span className="text-zinc-500">{tMarket("explainer.reasonLabel")}: </span>
            {explainer.reason}
          </div>
          {explainer.noDealHint && (
            <div
              data-testid="valuation-explainer-no-deal"
              className="text-[11px] text-zinc-500 italic pt-0.5"
            >
              {explainer.noDealHint}
            </div>
          )}
        </div>
      )}
      {insightText && (
        <div
          data-testid={`listing-detail-ai-insight-${card.id}`}
          className={`mt-2 rounded-xl border px-4 py-3 ${
            vertical === "auto"
              ? "border-amber-500/30 bg-amber-500/[0.04]"
              : "border-emerald-500/30 bg-emerald-500/[0.04]"
          }`}
        >
          <div
            className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] mb-1 ${
              vertical === "auto" ? "text-amber-300/80" : "text-emerald-300/80"
            }`}
          >
            <span aria-hidden="true">{vertical === "auto" ? "✦" : "🏠"}</span>
            DealAlert Insight
          </div>
          <div
            className={`text-sm leading-snug ${
              vertical === "auto" ? "text-amber-100" : "text-emerald-100"
            }`}
          >
            {insightText}
          </div>
        </div>
      )}
      {showComparables && (
        <button
          type="button"
          data-testid="view-comparables-btn"
          onClick={() => setCompOpen(true)}
          className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-400/60 rounded-full px-3 py-1.5 transition-colors"
        >
          {tMarket("comparables.view")}
          <span aria-hidden="true">→</span>
        </button>
      )}
      {showComparables && (
        <ComparablesModal
          ulid={card.id}
          vertical={card.vertical}
          open={compOpen}
          onOpenChange={setCompOpen}
        />
      )}
    </div>
  );
}

// 23.10.2026 — „Verfügbar ab" kommt als ISO-Datum aus der Quelle.
// Ungültiges bleibt leer statt „Invalid Date".
function formatDateShort(value, language) {
  if (typeof value !== "string" || !value.trim()) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const locale = language === "hr" ? "hr-HR" : language === "en" ? "en-GB" : "de-AT";
  return d.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}

/* -------- main component -------- */
export default function ListingDetailPage() {
  const { t, i18n } = useTranslation("listing");
  const { t: tDiscover } = useTranslation("discover");
  const { ulid } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  // Phase H2 — collapsible description (default collapsed on mobile).
  const [descExpanded, setDescExpanded] = useState(false);
  // `?ai=open` deep-link from DealCard's "KI fragen" button. The
  // AIAdvisorPanel reacts to this prop by scrolling into view + a
  // 1.5s emerald ring accent. Captured to local state so the panel
  // only reacts ONCE per navigation (not on every query update).
  const [aiOpenSignal, setAiOpenSignal] = useState(searchParams.get("ai") === "open");
  // SHIP B.6 — Lead CTA modal state + snapshot of the calculator's
  // current (inputs, output) for pre-fill into the lead payload.
  // Sprint F ADD-ON P0 — `showFinance` gates BOTH the calculator AND
  // the lead-CTA buttons. For rent listings the calculator made no
  // sense ("Kaufpreis 688 €" for a Miete-listing was a trust-killer
  // reported by users in production). Conservatively NULL/undefined
  // transaction_type (typical for autos) still shows finance — only
  // explicit "rent" suppresses it.
  const [leadOpen, setLeadOpen] = useState(false);
  const financingSnapshotRef = React.useRef({ inputs: null, output: null });
  React.useEffect(() => {
    if (searchParams.get("ai") === "open") setAiOpenSignal(true);
  }, [searchParams]);

  // Track which listing-ulid we've already auto-scrolled-to-top for —
  // used inside the post-data effect below. Declared up here so React
  // ordering is stable.
  const scrolledOncePerListingRef = React.useRef(null);

  const {
    data: card,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["listing", ulid],
    queryFn: () => getListing(ulid),
    enabled: Boolean(ulid),
    retry: false,
  });

  // Meta Pixel Sprint 2026-08 — fire ``ViewContent`` exactly once
  // per ulid, only after the listing successfully loaded and only
  // once per browser-session-per-ulid (dedupe via a ref set). Params
  // are pruned to the Meta-standard shape:
  //   * ``content_ids: [ulid]``
  //   * ``content_type: 'listing'``
  //   * ``content_category: vertical`` (auto / real_estate)
  // NO title, NO email, NO location text — the wrapper's allowlist
  // additionally guarantees only these keys survive the outbound
  // sanitiser.
  const _viewContentFiredRef = React.useRef(new Set());
  React.useEffect(() => {
    if (isLoading || isError) return;
    if (!card || !card.id) return;
    if (_viewContentFiredRef.current.has(card.id)) return;
    _viewContentFiredRef.current.add(card.id);
    import("../lib/metaPixel").then(({ track }) => {
      track("ViewContent", {
        content_ids: [String(card.id)],
        content_type: "listing",
        content_category: String(card.vertical || "").slice(0, 32),
      });
    });
  }, [card?.id, card?.vertical, isLoading, isError]);

  // Phase H2-followup — Force scroll-to-top whenever a NEW listing is
  // loaded UNLESS the user explicitly deep-linked into the AI section
  // (`?ai=open`). Without this, the AIAdvisorPanel's "scroll to last
  // message on initial load" effect snaps the whole page to the bottom
  // of the chat region, hiding the hero image + title from the user.
  // The ref guard ensures we only do this ONCE per ulid (so subsequent
  // re-renders don't fight user scrolling).
  //
  // P0.4c Punkt 3 — a back-navigation FROM a comparable listing to
  // the source deal restores the exact scroll offset + auto-opens the
  // advisor panel via the `advisor-return:{ulid}` sessionStorage
  // snapshot. This must run BEFORE the scroll-to-top branch so the
  // scroll restore wins.
  // Klagenfurt P0.4d Punkt 1 — chat scroll restore lives in the
  // panel itself (see `restoreSignal` + `onRestoreComplete` below).
  // The listing-page effect only decides WHICH branch to run:
  //
  //   * `wasOpen === true` (widget-path click) → forward the snapshot
  //     into the AIAdvisorPanel via `restoreSignal`. The panel
  //     restores the exact chat-container `scrollTop` /
  //     `anchorTurnUlid` AFTER hydrating turns, then invokes
  //     `onRestoreComplete` and we clear the sessionStorage entry.
  //     STATE IS NEVER CLEARED PREEMPTIVELY.
  //   * `wasOpen === false` (rental-comparables Dialog path) → the
  //     advisor is NOT force-opened. We restore only the host-page
  //     `window.scrollY` and clear the state.
  //
  // NB (iter94 fix): The return-state MUST be consumed BEFORE the
  // once-per-listing scroll-guard. When the SPA already visited the
  // source listing in this session the ref is set, causing the
  // guard to skip the effect and leave the return-state stranded in
  // sessionStorage. We flip the order — always check for a snapshot
  // first; only skip when there is no snapshot AND the guard has
  // fired.
  const [chatRestoreSignal, setChatRestoreSignal] = useState(null);
  React.useEffect(() => {
    if (!card) return;
    if (searchParams.get("ai") === "open") return;

    const returnState = readAdvisorReturnState(ulid);
    if (returnState && returnState.wasOpen) {
      // Widget-path → hand the exact chat-scroll snapshot to the
      // panel. The panel's restore effect will re-scroll AFTER
      // messages hydrate and only THEN signal completion.
      // Mark the listing as seen so a subsequent same-listing
      // re-render doesn't scroll the whole page to the top.
      scrolledOncePerListingRef.current = ulid;
      setAiOpenSignal(true);
      setChatRestoreSignal({
        // A monotonic requestId decouples the effect from React
        // identity — re-navigating the same source restarts restore.
        requestId: `restore-${ulid}-${Date.now()}`,
        // P0.4d cleanup contract — the panel MUST verify that the
        // same conversation is actually loaded before signalling
        // restore-complete (otherwise the anchor targets a foreign
        // conversation and we would clear the state prematurely).
        conversationId: returnState.conversationId || null,
        chatScrollTop:
          typeof returnState.chatScrollTop === "number"
            ? returnState.chatScrollTop
            : undefined,
        anchorTurnUlid: returnState.anchorTurnUlid || null,
        anchorTurnIndex:
          typeof returnState.anchorTurnIndex === "number"
            ? returnState.anchorTurnIndex
            : null,
      });
      // The state itself is cleared in `handlePanelRestoreComplete`
      // AFTER the panel confirms a successful restore.
      return;
    }
    if (returnState) {
      // Modal path — no advisor open-force. Restore host-page scroll
      // only. Safe to clear immediately: nothing else consumes the
      // state on this branch.
      scrolledOncePerListingRef.current = ulid;
      const y = Math.max(0, Number(returnState.scrollY) || 0);
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
        if (document.documentElement)
          document.documentElement.scrollTop = y;
        if (document.body) document.body.scrollTop = y;
      });
      clearAdvisorReturnState(ulid);
      return;
    }

    // No return snapshot → apply the once-per-listing scroll-to-top
    // guard so navigating between two listings doesn't repeatedly
    // yank the viewport on every card re-fetch.
    if (scrolledOncePerListingRef.current === ulid) return;
    scrolledOncePerListingRef.current = ulid;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [ulid, searchParams, card]);

  const handlePanelRestoreComplete = React.useCallback(() => {
    // The panel confirmed the scrollTop / anchor landed. NOW we may
    // drop the sessionStorage snapshot (P0.4d cleanup contract).
    if (ulid) clearAdvisorReturnState(ulid);
    setChatRestoreSignal(null);
  }, [ulid]);

  // Cheap "is favorited?" check — look in the favorites cache, fall back to fetch.
  const { data: favData } = useQuery({
    queryKey: ["favorites", "all"],
    queryFn: () => listFavorites({ limit: 200 }),
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const isFavorite = Boolean(
    favData?.items?.some((it) => it.id === ulid),
  );

  const fav = useMutation({
    mutationFn: () =>
      isFavorite ? removeFavorite(ulid) : addFavorite(ulid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favorites"] });
    },
    onError: (err) => {
      // Phase 1 — Free tier favourite limit (backend 402 free_limit_reached)
      if (err?.response?.status === 402) {
        const limit = err?.response?.data?.error?.details?.limit ?? 10;
        toast.error(tDiscover("gate.freeLimit.favorites", { limit }));
      }
    },
  });

  if (isLoading) {
    return <div className="text-zinc-500" data-testid="listing-detail-loading">{t("loading")}</div>;
  }
  if (isError) {
    const status = error?.response?.status;
    return (
      <div
        data-testid="listing-detail-error"
        className="border border-zinc-800 bg-zinc-950/60 rounded-2xl p-10 text-center"
      >
        <h2 className="text-xl text-zinc-100 mb-2">
          {status === 404 ? t("notFoundTitle") : t("loadErrorTitle")}
        </h2>
        <p className="text-sm text-zinc-400 mb-6">
          {status === 404
            ? t("notFoundBody")
            : t("loadErrorBody")}
        </p>
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="px-4 py-2 rounded-lg bg-amber-500 text-zinc-950 text-sm font-medium hover:bg-amber-400"
        >
          {t("backToResults", { defaultValue: "Zurück" })}
        </button>
      </div>
    );
  }
  if (!card) return null;

  const vertical = card.vertical;
  const images = card.images && card.images.length ? card.images : [];

  // Sprint F ADD-ON P0 — Single source of truth for "is this listing a
  // buy-side (finance-relevant) listing?". Used to gate the calculator
  // AND the lead-CTA buttons in the sidebar / inline below the
  // calculator. Conservative: only EXPLICIT "rent" suppresses finance —
  // null/undefined (typical for autos) still shows.
  //
  // Aufgabe 11 (06.10.2026): the `user` requirement is GONE. The
  // calculator and the financing request work without an account; only
  // saving a calculation to the personal history needs a login.
  const showFinance = Boolean(
    card?.price && card?.transaction_type !== "rent"
  );

  return (
    <div data-testid="listing-detail-page" className="space-y-8">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/dashboard"
          data-testid="listing-back-link"
          className="text-sm text-zinc-400 hover:text-zinc-200 underline-offset-4 hover:underline"
        >
          {t("back")}
        </Link>
        <div className="flex items-center gap-2">
          <ShareDealButton listingUlid={card.id} variant="icon" />
          <button
            type="button"
            data-testid="listing-favorite-toggle"
            onClick={() => fav.mutate()}
            disabled={fav.isPending}
            className={`px-3 py-1.5 rounded-lg border text-sm transition ${
              isFavorite
                ? "bg-rose-500/20 border-rose-400 text-rose-300"
                : "border-zinc-700 text-zinc-200 hover:border-rose-400 hover:text-rose-300"
            }`}
          >
            {isFavorite ? `♥ ${t("favorited")}` : `♡ ${t("favorite")}`}
          </button>
          {card.url && (
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="listing-open-source"
              onClick={() => trackFunnel("source_open", { ulid: card.id })}
              className="px-3 py-1.5 rounded-lg bg-amber-500 text-zinc-950 text-sm font-medium hover:bg-amber-400"
            >
              {t("openOn", { platform: card.source_platform })}
            </a>
          )}
        </div>
      </div>

      {card.status === "inactive" && (
        <div
          data-testid="listing-inactive-banner"
          className="flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] px-4 py-3"
        >
          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-400/80" />
          <div>
            <p className="text-sm font-medium text-amber-200">{t("inactiveTitle")}</p>
            <p className="text-xs text-zinc-400">{t("inactiveBody")}</p>
          </div>
        </div>
      )}

      {/* P1 — 2-Spalten 65/35 Premium-Layout. Reverted from the
          previous 3-column experiment which felt cramped at common
          desktop widths (1280-1440 px). Below 1024 px everything
          stacks; the order is tuned so the user reads:
            Galerie → Title/Price → Insight → Finance → Beschreibung
            → Market → Hard Facts → Risks → Vision → Admin
            → AI Advisor (sidebar collapses into stack) → CTA → Save → Share */}
      {/* Sprint F — Variante B (Co-Pilot 50:50). Sprint-F-ADD-ON
          widened the right column further (from 11:9 to 1:1) so the
          AI Advisor reads as a co-pilot, not a side widget. Aside
          lost its `max-h / overflow-y-auto / pr-1` scroll-container
          so we end up with a SINGLE inner scroll inside the
          conversation div — fewer nested scrolls, calmer sticky
          behaviour. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(480px,1fr)] gap-6">
        {/* ---------- LEFT (~50%) — All content sections ---------- */}
        <div className="space-y-5 order-1 lg:order-1 min-w-0">
          <ImageStrip
            images={images}
            primary={card.primary_image_url}
            health={card.image_health}
            listing={card}
            isAdmin={isAdmin}
          />
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                data-testid={`detail-vertical-${vertical}`}
                className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[10px] uppercase tracking-wider"
              >
                {vertical === "auto" ? "Auto" : "Immo"}
              </span>
              {/* Image-health title-row badges are admin-only — users
                  never see "image broken" / "unverified" jargon. */}
              {isAdmin && card.image_health === "dead_on_check" && (
                <span
                  data-testid="detail-image-broken-badge"
                  className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 text-[10px] uppercase tracking-wider"
                >
                  ⚠ image broken
                </span>
              )}
              {isAdmin && card.image_health === "unchecked" && (
                <span
                  data-testid="detail-image-unverified-badge"
                  className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-300 text-[10px] uppercase tracking-wider"
                >
                  ? unverified
                </span>
              )}
            </div>
            <h1
              data-testid="listing-detail-title"
              className="text-2xl text-zinc-100 font-medium leading-tight"
            >
              {card.title || "(ohne Titel)"}
            </h1>
            {card.subtitle && (
              <div className="text-sm text-zinc-400 mt-1">{card.subtitle}</div>
            )}
          </div>

          <div className="flex items-baseline gap-3 flex-wrap">
            <div
              data-testid="listing-detail-price"
              className="text-3xl text-amber-400 font-medium"
            >
              {card.price_display || "—"}
            </div>
            {card.location && (
              <div className="text-sm text-zinc-500">· {card.location}</div>
            )}
          </div>

          {/* Master-Sprint P1 — real price history (observed changes +
              source-documented reduction). Renders NOTHING when no real
              data exists — nothing is derived artificially. */}
          <PriceHistorySection ulid={ulid} />

          {/* V4.11 — High-Value-Feature-Badges (zentrale Taxonomie,
              server-seitig priorisiert: Meerblick/Pool/PV bzw. 360°/HUD
              zuerst). Gleiche Optik wie auf den DealCards. */}
          {card.feature_slugs && card.feature_slugs.length > 0 && (
            <div
              data-testid="listing-detail-feature-badges"
              className="flex flex-wrap gap-1.5"
            >
              {card.feature_slugs.slice(0, 5).map((slug) => (
                <span
                  key={slug}
                  className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300/90 text-xs"
                >
                  {featureLabel(slug)}
                </span>
              ))}
            </div>
          )}

          {/* V4.2 P0 — Damage / accident warn badge. Rendered ABOVE
              everything else so the risk hits the user first. */}
          <DamageWarnBadge flags={card.damage_flags} />

          {/* V4.15 P0 reorder — Standort-Badge FIRST: the user anchors
              the listing geographically before reading the market
              verdict. The MiniMap moved further down (after the hard
              facts) so the reading flow is: Standort → Marktsignal →
              Insight → Daten → Karte → Beschreibung → Finanzierung. */}
          <Section title={t("location")} testid="location-section">
            {(() => {
              const ld = card.location_details || {};
              const lineMain = [
                [ld.postal_code, ld.city].filter(Boolean).join(" "),
                ld.district,
                ld.region,
              ]
                .filter(Boolean)
                .join(" · ");
              const display = lineMain || card.location || null;
              return (
                <div
                  data-testid="location-details-row"
                  className="border border-zinc-800 bg-zinc-950/60 rounded-2xl px-5 py-3"
                >
                  {ld.street && (
                    <div className="text-sm text-zinc-100">{ld.street}</div>
                  )}
                  {display ? (
                    <div className="text-sm text-zinc-400">{display}</div>
                  ) : (
                    <div className="text-sm text-zinc-500 italic">
                      {t("locationUnavailable")}
                    </div>
                  )}
                </div>
              );
            })()}
          </Section>

          <MarketSignalBlock card={card} />

          {/* V4.23 — precomputed rent-estimate + legal rent-control
              warning (visible BEFORE the user opens the AI advisor). */}
          <RentEstimateBlock card={card} />

          {/* Master-Audit 2026-07 — the standalone DealAlert-Insight card
              was merged INTO the MarketSignalBlock above (it duplicated
              the market verdict almost verbatim as a sibling card). */}

          {/* V4.15 P0 reorder — Mini-Map now lives AFTER the hard
              facts: the user absorbs the data first, then sees the
              geographic context, then reads the prose description. */}
          {vertical === "auto" && (
            <Section title={t("autoFacts")} testid="auto-facts">
              <div className="border border-zinc-800 bg-zinc-950/60 rounded-2xl px-5 py-3">
                <KvRow k={t("facts.make")} v={card.make} />
                <KvRow k={t("facts.modelFamily")} v={card.model_family} />
                <KvRow k={t("facts.model")} v={card.model} />
                <KvRow k={t("facts.series")} v={card.model_series} />
                <KvRow k={t("facts.year")} v={card.year} />
                <KvRow
                  k={t("facts.mileage")}
                  v={
                    card.mileage_km != null
                      ? `${card.mileage_km.toLocaleString("de-AT")} km`
                      : null
                  }
                />
                <KvRow k={t("facts.fuel")} v={enumLabel("fuel", card.fuel)} />
                <KvRow k={t("facts.gearbox")} v={enumLabel("gearbox", card.gearbox)} />
                <KvRow
                  k={t("facts.power")}
                  v={card.power_kw != null ? `${card.power_kw} kW` : null}
                />
                <KvRow k={t("facts.bodyType")} v={enumLabel("body_type", card.body_type)} />
                <KvRow k={t("facts.trim")} v={card.trim} />
                <KvRow
                  k={t("facts.seller")}
                  v={card.seller_type ? enumLabel("seller_type", card.seller_type) : null}
                />
              </div>
            </Section>
          )}

          {vertical === "immo" && (
            <Section title={t("immoFacts")} testid="immo-facts">
              <div className="border border-zinc-800 bg-zinc-950/60 rounded-2xl px-5 py-3">
                <KvRow
                  k={t("facts.transaction")}
                  v={enumLabel("transaction_type", card.transaction_type) || null}
                />
                <KvRow
                  k={t("facts.objectType")}
                  v={
                    card.object_type && card.object_type !== "unknown"
                      ? enumLabel("object_type", card.object_type)
                      : null
                  }
                />
                <KvRow
                  k={t("facts.livingArea")}
                  v={card.living_area_m2 != null ? `${card.living_area_m2} m²` : null}
                />
                <KvRow
                  k={t("facts.weightedArea")}
                  v={card.weighted_area_m2 != null ? `${card.weighted_area_m2} m²` : null}
                />
                <KvRow
                  k={t("facts.rooms")}
                  v={card.rooms != null && card.rooms > 0 ? card.rooms : null}
                />
                <KvRow
                  k="€/m²"
                  v={card.price_per_m2 != null ? `${card.price_per_m2}` : null}
                />
                <KvRow
                  k={t("facts.rentPerM2")}
                  v={card.rent_per_m2 != null ? `${card.rent_per_m2}` : null}
                />
                <KvRow
                  k={t("facts.outdoor")}
                  v={
                    card.has_outdoor_space === true
                      ? t("facts.outdoorYes")
                      : card.has_outdoor_space === false
                        ? t("facts.outdoorNo")
                        : null
                  }
                />
                <KvRow
                  k={t("facts.features")}
                  v={(card.feature_slugs_true || card.feature_slugs || [])
                    .map((slug) => featureLabel(slug))}
                />
                {/* 23.10.2026 — strukturierte Portalangaben, die bisher
                    nur in der Quelle standen. `null` = keine Angabe. */}
                <KvRow
                  k={t("facts.floor")}
                  v={card.floor != null ? card.floor : null}
                />
                <KvRow
                  k={t("facts.totalFloors")}
                  v={card.total_floors != null ? card.total_floors : null}
                />
                <KvRow
                  k={t("facts.unitLevels")}
                  v={
                    card.unit_levels === 1
                      ? t("facts.singleLevel")
                      : card.unit_levels != null
                        ? t("facts.levelsCount", { count: card.unit_levels })
                        : null
                  }
                />
                <KvRow
                  k={t("facts.yearLastRenovated")}
                  v={card.year_last_renovated != null ? card.year_last_renovated : null}
                />
                <KvRow
                  k={t("facts.parkingSpaces")}
                  v={card.parking_spaces != null ? card.parking_spaces : null}
                />
                <KvRow
                  k={t("facts.elevator")}
                  v={
                    card.has_elevator === true
                      ? t("facts.yes")
                      : card.has_elevator === false
                        ? t("facts.no")
                        : null
                  }
                />
                <KvRow
                  k={t("facts.furnished")}
                  v={
                    card.is_furnished === true
                      ? t("facts.yes")
                      : card.is_furnished === false
                        ? t("facts.no")
                        : null
                  }
                />
                <KvRow
                  k={t("facts.availableFrom")}
                  v={formatDateShort(card.available_from, i18n.language)}
                />
                <KvRow
                  k={t("facts.availableYearRound")}
                  v={
                    card.available_year_round === true
                      ? t("facts.yes")
                      : card.available_year_round === false
                        ? t("facts.seasonalOnly")
                        : null
                  }
                />
                <KvRow
                  k={t("facts.yield")}
                  v={
                    isPlausibleYield(card.yield_pct)
                      ? `${card.yield_pct}%`
                      : null
                  }
                />
              </div>
            </Section>
          )}

          <Section title={t("mapTitle")} testid="listing-map-section">
            <MiniMap
              lat={card.location_details?.latitude}
              lng={card.location_details?.longitude}
              approxLat={card.location_details?.approx_latitude}
              approxLng={card.location_details?.approx_longitude}
              approxSource={card.location_details?.approx_source}
              ulid={card.id}
              vertical={vertical === "auto" ? "auto" : "immo"}
              country={card.country}
              geoPrecision={card.location_details?.geo_precision}
              locationLabel={
                [
                  card.location_details?.postal_code,
                  card.location_details?.city,
                ]
                  .filter(Boolean)
                  .join(" ") || card.location
              }
            />
          </Section>

          {card.description && (
            <Section
              title={t("description")}
              testid="listing-original-description"
            >
              <div className="border border-zinc-800 bg-zinc-950/60 rounded-2xl px-5 py-4">
                <p
                  data-testid="listing-original-description-body"
                  className={`text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed ${
                    descExpanded ? "" : "line-clamp-4"
                  }`}
                >
                  {card.description}
                </p>
                {(() => {
                  const desc = String(card.description);
                  const newlineCount = (desc.match(/\n/g) || []).length;
                  const longEnough = desc.length > 140 || newlineCount >= 3;
                  if (!longEnough) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => setDescExpanded((v) => !v)}
                      data-testid="description-toggle"
                      className="mt-2 text-xs text-emerald-400 underline-offset-4 hover:underline"
                    >
                      {descExpanded ? t("showLess") : t("showMore")}
                    </button>
                  );
                })()}
              </div>
            </Section>
          )}

          {vertical === "auto" && riskFlagLabels(card.risk_flags).length > 0 && (
            <Section title={t("riskFlags")} testid="risk-flags">
              <div className="flex flex-wrap gap-2">
                {riskFlagLabels(card.risk_flags).map((label) => (
                  <span
                    key={label}
                    className="px-2.5 py-1 rounded bg-amber-500/15 text-amber-300 text-xs"
                  >
                    ⚠ {label}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* DealAlert Vision (Phase Vision-V1) — only surfaces when the
              Vision Worker has actually produced features for this
              listing. Plain factual list, no jargon, no admin-only
              metadata. */}
          {card.vision_features && (
            <Section title={t("vision")} testid="vision-findings">
              <div
                data-testid="vision-findings-block"
                className="border border-zinc-800 bg-zinc-950/60 rounded-2xl px-5 py-4 space-y-2"
              >
                {vertical === "immo" && (
                  <ul className="text-sm text-zinc-300 space-y-1">
                    {["pool", "balcony", "terrace", "garden"].map((k) => {
                      const v = card.vision_features?.[k];
                      if (!v || typeof v !== "object") return null;
                      const labels = {
                        pool: t("visionFeature.pool"),
                        balcony: t("visionFeature.balcony"),
                        terrace: t("visionFeature.terrace"),
                        garden: t("visionFeature.garden"),
                      };
                      return (
                        <li key={k} className="flex items-center justify-between">
                          <span className="text-zinc-400">{labels[k]}</span>
                          <span
                            className={
                              v.detected
                                ? "text-emerald-300"
                                : "text-zinc-500"
                            }
                          >
                            {v.detected ? t("detected") : t("notDetected")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {vertical === "auto" && card.vision_features?.condition_score && (
                  <div className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">{t("condition")}</span>
                      <span className="text-zinc-200">
                        {card.vision_features.condition_score} / 10
                      </span>
                    </div>
                    {Array.isArray(card.vision_features.damage) && card.vision_features.damage.length > 0 && (
                      <div className="mt-1 text-xs text-amber-300/90">
                        {t("damageHints", { count: card.vision_features.damage.length })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* P1 section reorder: Finance Calculator moved to the END of
              the left column. The right-sidebar Lead-CTA + AI Advisor are
              the primary actions; finance is supporting detail the user
              reaches AFTER they've absorbed all the listing facts.

              Sprint D fix — Finanzierungs-CTA Regression:
              The sidebar `Finanzierung anfragen` button is sticky on
              desktop, but on mobile it collapses BELOW the calculator
              + admin section, so the user reads the financing inputs
              without seeing the conversion CTA in the same eye-line.
              We add an INLINE CTA directly under the calculator that
              opens the same `LeadModal` — duplicate conversion path
              with no extra state, the snapshot ref is already wired. */}
          {/* Sprint F ADD-ON P0 — Finance Calculator is BUY-ONLY.
              For `transaction_type === "rent"` (Miete) the calculator
              made zero sense ("Kaufpreis 688 € — Monatsrate 3 €" for
              a rental was a trust-killer). We gate the calculator,
              the inline lead-CTA, AND the sidebar lead-CTA on a
              single `showFinance` flag derived in render-scope.

              Rules:
              - Must be logged-in user
              - Must have a price (otherwise calculator math fails)
              - `transaction_type` must NOT be "rent" — null/undefined
                (typical for autos) still shows the calculator, which
                is the legacy expected behaviour. */}
          {showFinance && (
            <FinanceCalculator
              card={card}
              onResult={(out, inputs) => {
                financingSnapshotRef.current = { inputs, output: out };
              }}
            />
          )}
          {showFinance && (
            <button
              type="button"
              onClick={() => setLeadOpen(true)}
              data-testid="lead-cta-inline-finance"
              className="w-full rounded-2xl border border-amber-500/60 bg-amber-500 text-zinc-950 px-5 py-3.5 text-sm font-semibold transition hover:bg-amber-400 hover:border-amber-300 flex items-center justify-center gap-2"
            >
              Finanzierung anfragen →
            </button>
          )}

          {isAdmin && (
            <Section title={t("adminDetails")} testid="listing-meta">
              <details className="group border border-zinc-800 bg-zinc-950/60 rounded-2xl px-5 py-3">
                <summary
                  className="cursor-pointer select-none text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-300"
                  data-testid="listing-meta-toggle"
                >
                  Anzeigen / Verbergen
                </summary>
                <div className="mt-3 space-y-1">
                  <KvRow k="Listing ULID" v={card.ulid} />
                  <KvRow k="Source" v={card.source_platform} />
                  <KvRow k="Market" v={card.source_market} />
                  <KvRow k="Country" v={card.country} />
                  <KvRow
                    k="First seen"
                    v={card.created_at ? new Date(card.created_at).toLocaleString() : null}
                  />
                  <KvRow
                    k="Last seen"
                    v={card.last_seen_at ? new Date(card.last_seen_at).toLocaleString() : null}
                  />
                  <KvRow k="Image health" v={card.image_health} />
                  <KvRow
                    k="Image checked"
                    v={
                      card.image_health_checked_at
                        ? new Date(card.image_health_checked_at).toLocaleString()
                        : null
                    }
                  />
                </div>
              </details>
            </Section>
          )}
        </div>

        {/* ---------- RIGHT (~35%, sticky) — Action sidebar ----------
            Mobile: stacks below the content; desktop: sticks under the
            top nav so the AI Advisor + CTAs are always one scroll away.
            Finance moved out — it now lives in the main column for a
            calmer composition.

            Sprint D fix — AI Advisor sticky regression:
            - `lg:z-20` lifted the sidebar above any in-content cards
              that may have their own z-context (price banners, image
              modals). Without an explicit z-index sticky elements
              ALWAYS create a stacking context but at the natural
              auto-level, so a heavy content card with `z-10` could
              visually pass on top.
            - `lg:top-[5rem]` swapped from the bare `lg:top-20` so
              Tailwind JIT picks up the exact pixel value even if a
              future arbitrary-spacing config truncates the safelist.
            - Sprint F Variante B — DROPPED the
              `max-h-[calc(100vh-6rem)] overflow-y-auto pr-1` triple
              that turned the aside into its own scroll container.
              Now there is exactly ONE inner scroll (the AI
              conversation div). Sticky pins the whole aside at
              top:5rem and the user mouse-wheels the page underneath
              without scroll-conflict. */}
        <aside
          className="order-2 lg:order-2 space-y-3 min-w-0 hidden lg:flex lg:flex-col
                     lg:sticky lg:top-[5rem] lg:z-20 lg:self-start lg:max-h-[calc(100vh-6.5rem)]"
          data-testid="listing-detail-sidebar"
        >
          <AIAdvisorPanel
            listingUlid={card.id}
            vertical={card.vertical}
            country={card.country}
            openSignal={aiOpenSignal}
            card={card}
            restoreSignal={chatRestoreSignal}
            onRestoreComplete={handlePanelRestoreComplete}
          />

          {/* Action-Row — 2-column compact layout under the
              Co-Pilot panel so the buttons feel like an aligned
              footer, not 3 stacked CTAs. Finance gets the priority
              column for visual weight (amber accent).
              Sprint F P0 — for rentals (`showFinance === false`)
              the Action-Row degrades to a single full-width Favorite
              toggle. We don't show a "Mietanfrage stellen" button
              yet because the rent-side lead pipeline doesn't exist
              (see `RENT_MODULES_BACKLOG.md`). */}
          {showFinance ? (
            <div className="grid grid-cols-5 gap-2 lg:shrink-0">
              <button
                type="button"
                onClick={() => setLeadOpen(true)}
                data-testid="lead-cta-button"
                className="col-span-3 rounded-2xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-300 transition hover:bg-amber-500/20"
              >
                {t("financing")}
              </button>
              <button
                type="button"
                data-testid="sidebar-favorite-toggle"
                onClick={() => fav.mutate()}
                disabled={fav.isPending}
                className={`col-span-2 rounded-2xl border px-3 py-3 text-sm transition flex items-center justify-center gap-1.5 ${
                  isFavorite
                    ? "bg-rose-500/15 border-rose-400/60 text-rose-300 hover:bg-rose-500/20"
                    : "border-zinc-800 bg-zinc-950/60 text-zinc-200 hover:border-rose-400/60 hover:text-rose-300"
                }`}
              >
                <span className="text-base leading-none">{isFavorite ? "♥" : "♡"}</span>
                <span className="text-xs">{isFavorite ? t("favorited") : t("favorite")}</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="sidebar-favorite-toggle"
              onClick={() => fav.mutate()}
              disabled={fav.isPending}
              className={`w-full rounded-2xl border px-5 py-3 text-sm transition lg:shrink-0 flex items-center justify-center gap-2 ${
                isFavorite
                  ? "bg-rose-500/15 border-rose-400/60 text-rose-300 hover:bg-rose-500/20"
                  : "border-zinc-800 bg-zinc-950/60 text-zinc-200 hover:border-rose-400/60 hover:text-rose-300"
              }`}
            >
              <span className="text-base leading-none">{isFavorite ? "♥" : "♡"}</span>
              {isFavorite ? t("saved") : t("save")}
            </button>
          )}

          <button
            type="button"
            data-testid="sidebar-share-button"
            onClick={async () => {
              const url = typeof window !== "undefined" ? window.location.href : "";
              const shareData = {
                title: card.title || "DealAlert Inserat",
                text: card.title
                  ? `${card.title} · ${card.price_display || ""}`.trim()
                  : "DealAlert Inserat",
                url,
              };
              if (canUseNativeShare()) {
                try {
                  await navigator.share(shareData);
                  return;
                } catch (err) {
                  // User dismissed the sheet — do NOT copy the link.
                  if (err?.name === "AbortError" || err?.name === "InvalidStateError") return;
                  // Real failure — fall through to copy.
                }
              }
              try {
                await navigator.clipboard.writeText(url);
                toast.success(t("linkCopied"));
              } catch {
                toast.error(t("linkCopyFailed"));
              }
            }}
            className="w-full lg:shrink-0 rounded-2xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-5 py-2.5 text-xs transition hover:border-emerald-400/60 hover:text-emerald-300 flex items-center justify-center gap-2"
          >
            <Share2 className="w-3.5 h-3.5" />
            {t("share")}
          </button>
        </aside>
      </div>
      {/* Sprint F — Mobile FAB-Drawer for AI Advisor.
          Renders only on `<lg` viewports where the side-by-side
          layout collapses. Pins the Co-Pilot one tap away so the
          AI feels persistent on touch devices, not buried at the
          bottom of a long scroll. */}
      <AIAdvisorMobileFab
        listingUlid={card.id}
        vertical={card.vertical}
        openSignal={aiOpenSignal}
        card={card}
        restoreSignal={chatRestoreSignal}
        onRestoreComplete={handlePanelRestoreComplete}
      />
      {/* SHIP B.6 — LeadModal portal */}
      <LeadModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        card={card}
        financingInputs={financingSnapshotRef.current?.inputs}
        financingOutput={financingSnapshotRef.current?.output}
      />
    </div>
  );
}
